import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { store, persistor } from './store';
import theme from './theme';
import { UserRole } from './types/auth.types';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/dashboard/Dashboard';
import UserProfile from './components/profile/UserProfile';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Unauthorized from './components/common/Unauthorized';
import UserManagement from './components/admin/UserManagement';
import SystemConfiguration from './components/admin/SystemConfiguration';
import DepartmentReports from './components/admin/DepartmentReports';
import GeneralManagerReports from './components/admin/GeneralManagerReports';
import FinanceReports from './components/admin/FinanceReports';
import EmployeeReports from './components/admin/EmployeeReports';
import CompanySettings from './components/admin/CompanySettings';

const App: React.FC = () => {
  console.log('App is rendering with full authentication system!');
  
  return (
    <Provider store={store}>
      <PersistGate 
        loading={
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress />
          </Box>
        } 
        persistor={persistor}
      >
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/config"
                  element={
                    <ProtectedRoute>
                      <SystemConfiguration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/company"
                  element={
                    <ProtectedRoute allowedRoles={[UserRole.SYSTEM_ADMIN]}>
                      <CompanySettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/reports"
                  element={
                    <ProtectedRoute>
                      <DepartmentReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gm-reports"
                  element={
                    <ProtectedRoute>
                      <GeneralManagerReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/finance-reports"
                  element={
                    <ProtectedRoute>
                      <FinanceReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee/reports"
                  element={
                    <ProtectedRoute allowedRoles={[UserRole.EMPLOYEE]}>
                      <EmployeeReports />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Router>
          </LocalizationProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;